<template>
  <div id="app" class="min-h-screen flex flex-col">
    <!-- Loading State -->
    <div v-if="!authStore.initialized" class="flex-1 flex items-center justify-center bg-wat-cream">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
        <p class="mt-4 text-gray-600">{{ $t('common.loading') }}</p>
      </div>
    </div>

    <!-- Main App -->
    <template v-else>
      <AppHeader v-if="!hideLayout" @open-login="showLoginModal = true" />
      <main class="flex-1">
        <router-view />
      </main>
      <AppFooter v-if="!hideLayout" />

      <!-- Auth Modal -->
      <AuthModal
        v-if="!hideLayout"
        :is-open="showLoginModal"
        @close="showLoginModal = false"
        @success="onLoginSuccess"
      />

      <CookieConsentBanner v-if="!hideLayout" />
      <PhotoAssignmentLimitModal
        v-if="!hideLayout"
        :is-open="cartStore.showPhotoAssignmentLimitModal"
        @close="cartStore.closePhotoAssignmentLimitModal"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import AuthModal from '@/components/AuthModal.vue'
import CookieConsentBanner from '@/components/CookieConsentBanner.vue'
import PhotoAssignmentLimitModal from '@/components/PhotoAssignmentLimitModal.vue'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { useI18nStore } from '@/stores/i18n'

const authStore = useAuthStore()
const cartStore = useCartStore()
const i18nStore = useI18nStore()
const route = useRoute()
const router = useRouter()
const showLoginModal = ref(false)

// Check if current route is admin route
const isAdminRoute = computed(() => {
  return route.path.startsWith('/admin')
})

// Check if we should hide header/footer (admin routes and error pages)
const hideLayout = computed(() => {
  // Hide on admin routes
  if (route.path.startsWith('/admin')) return true

  // Hide on error pages
  const errorPageNames = ['access-denied', 'not-found', 'error']
  if (errorPageNames.includes(route.name as string)) return true

  // Hide on error status code paths
  if (route.path.match(/^\/(422|404|500|403|401|400)$/)) return true

  // Hide if route has meta.hideLayout
  if (route.meta.hideLayout) return true

  return false
})

onMounted(async () => {
  // Initialize auth state from localStorage
  await authStore.initAuth()

  // Load user's preferred locale from backend
  await i18nStore.loadUserLocale()

  // Check if login is required from query param (redirected from admin)
  if (route.query.login === 'required' && !authStore.isAuthenticated) {
    showLoginModal.value = true
    // Remove the query param
    router.replace({ query: {} })
  }

  // Fetch cart from server
  // Server will create anonymous user and set cookie if needed
  await cartStore.fetchCart()
})

// Watch for query changes (in case of navigation without full reload)
watch(() => route.query.login, (newVal) => {
  if (newVal === 'required' && !authStore.isAuthenticated) {
    showLoginModal.value = true
    router.replace({ query: {} })
  }
})

function onLoginSuccess() {
  // Handle successful login - cart is already merged in loginWithGoogle
  console.log('Login successful!')

  // Check if there's a redirect path stored (e.g., from admin access attempt)
  const redirectPath = localStorage.getItem('redirect_after_login')
  if (redirectPath) {
    localStorage.removeItem('redirect_after_login')
    // Only redirect if user is staff, otherwise they'll be redirected to access-denied by the guard
    if (authStore.isStaff) {
      router.push(redirectPath)
    }
  }
}
</script>
